import { PolylineMeasurement } from '@technology/web3d-plugin-markup';
import {
  Measurement,
  SlopeFormat,
  PolylineMeasurementType,
  slopeFormatter,
  MeasurementInfoButtonConfig,
} from '@trimble/web3d-providers';
import {
  calculateDistance,
  calculateHorizontalDistance,
  calculateVerticalDistance,
  LengthFormatter,
  SlopeFormatter,
} from '@trimble/utilities';
import {
  createEffect,
  createMemo,
  createSignal,
  Show,
  type JSX,
} from 'solid-js';
import { LengthUnit } from '@fulcrum/lib';
import { t } from '@trimble/i18n';
import {
  MeasurementProperty,
  ResultRowProps,
} from '@trimble/measurements-shared';
import { FormattingSettings } from '@trimble/types';

interface SummaryData {
  totalLength: number;
  total2D: number;
  verticalDifference: number;
  averageVertical: number;
  maxVertical: number;
  minVertical: number;
  startToEndSlope: string;
  minSlope: string;
  maxSlope: string;
  averageSlope: string;
}

export interface PolylineMeasurementDetailsProps {
  measurement: Measurement | undefined;
  slopeFormat?: SlopeFormat;
  baseLengthUnit: LengthUnit;
  formattingSettings: FormattingSettings;
  onSave?(): void;
  infoButtonConfig?: MeasurementInfoButtonConfig;
  disableEdit?: boolean;
  disabledReason?: string;
}

export const PolylineMeasurementDetails = (
  props: PolylineMeasurementDetailsProps
): JSX.Element => {
  const [selectedSlopeFormat, setSelectedSlopeFormat] =
    createSignal<SlopeFormat>('ratio');
  const slopeDropdownOptions = [
    {
      label: t('shared.ui.unitSettings.slopeAngleFormatRatio'),
      value: 'ratio',
    },
    {
      label: t('shared.ui.unitSettings.slopeAngleFormatPercentage'),
      value: 'percentage',
    },
    {
      label: t('shared.ui.unitSettings.slopeAngleFormatDegrees'),
      value: 'degrees',
    },
  ];

  createEffect(() => {
    setSelectedSlopeFormat(props.slopeFormat || 'ratio');
  });

  const formatSlopeRatio = (ratio: number): string => {
    return SlopeFormatter.toDisplay({
      measuredValue: ratio,
      currentUnits: 'ratio',
      settings: { format: selectedSlopeFormat() },
    });
  };

  const handleSlopeFormatChange = (value: SlopeFormat): void => {
    setSelectedSlopeFormat(value);
    const measurement = props.measurement;
    if (!measurement) return;

    const markups = measurement.markups;
    if (markups.length === 0) return;

    (measurement as PolylineMeasurementType).slopeFormat = value;
    (markups[0] as PolylineMeasurement).slopeFormatter = (
      vertical,
      horizontal
    ) => slopeFormatter(vertical, horizontal, value);
    props.onSave?.();
  };

  const summaryData = createMemo((): SummaryData | null => {
    if (!props.measurement) return null;

    const segmentInfo = (
      props.measurement.markups[0] as PolylineMeasurement
    ).getAllSegmentsInfo();

    if (segmentInfo.length === 0) return null;

    let totalLength = 0;
    let total2D = 0;
    const verticalValues: number[] = [];
    const slopeValues: number[] = [];

    segmentInfo.forEach((segment) => {
      const segmentHorizontal = calculateHorizontalDistance(
        segment.startPointWorld,
        segment.endPointWorld
      );
      const segmentVertical = calculateVerticalDistance(
        segment.startPointWorld,
        segment.endPointWorld
      );
      const segment3D = calculateDistance(
        segment.startPointWorld,
        segment.endPointWorld
      );

      totalLength += segment3D;
      total2D += segmentHorizontal;
      verticalValues.push(segmentVertical);

      if (segmentHorizontal > 0) {
        slopeValues.push(segmentVertical / segmentHorizontal);
      }
    });

    const averageVertical =
      verticalValues.reduce((sum, val) => sum + val, 0) / verticalValues.length;
    const maxVertical = Math.max(...verticalValues);
    const minVertical = Math.min(...verticalValues);

    const firstPoint = segmentInfo[0].startPointWorld;
    const lastPoint = segmentInfo[segmentInfo.length - 1].endPointWorld;
    const startToEndVertical = lastPoint.z - firstPoint.z;

    const startToEndHorizontal = calculateHorizontalDistance(
      firstPoint,
      lastPoint
    );

    const startToEndSlope =
      startToEndHorizontal > 0
        ? formatSlopeRatio(startToEndVertical / startToEndHorizontal)
        : '0';

    const averageSlopeRatio =
      slopeValues.length > 0
        ? slopeValues.reduce((sum, ratio) => sum + ratio, 0) /
          slopeValues.length
        : 0;

    const averageSlope = formatSlopeRatio(averageSlopeRatio);

    const minSlopeRatio = slopeValues.length > 0 ? Math.min(...slopeValues) : 0;
    const maxSlopeRatio = slopeValues.length > 0 ? Math.max(...slopeValues) : 0;

    const minSlope = formatSlopeRatio(minSlopeRatio);
    const maxSlope = formatSlopeRatio(maxSlopeRatio);

    return {
      totalLength,
      total2D,
      verticalDifference: startToEndVertical,
      averageVertical,
      maxVertical,
      minVertical,
      startToEndSlope,
      minSlope,
      maxSlope,
      averageSlope,
    };
  });

  const distanceData = (data: SummaryData): ResultRowProps[] => [
    {
      cells: [
        {
          label: t('measurements.length'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.totalLength,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'total-length',
          valueTestId: 'total-length-value',
        },
        {
          label: t('measurements.2D'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.total2D,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'total-2d',
          valueTestId: 'total-2d-value',
        },
      ],
      testId: 'distance-values',
    },
  ];

  const verticalData = (data: SummaryData): ResultRowProps[] => [
    {
      cells: [
        {
          label: t('measurements.polyline.verticalDifference'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.verticalDifference,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'vertical-difference',
          valueTestId: 'vertical-difference-value',
        },
        {
          label: t('measurements.polyline.average'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.averageVertical,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'vertical-average',
          valueTestId: 'vertical-average-value',
        },
      ],
      testId: 'vertical-values-row-1',
    },
    {
      cells: [
        {
          label: t('measurements.polyline.max'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.maxVertical,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'vertical-max',
          valueTestId: 'vertical-max-value',
        },
        {
          label: t('measurements.polyline.min'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.minVertical,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'vertical-min',
          valueTestId: 'vertical-min-value',
        },
      ],
      testId: 'vertical-values-row-2',
    },
  ];

  const slopeAndGradeData = (data: SummaryData): ResultRowProps[] => [
    {
      cells: [
        {
          label: t('measurements.polyline.starttoend'),
          value: data.startToEndSlope,
          testId: 'start-to-end-slope',
          valueTestId: 'start-to-end-slope-value',
        },
      ],
      testId: 'slope-start-to-end',
    },
    {
      cells: [
        {
          label: t('measurements.polyline.min'),
          value: data.minSlope,
          testId: 'slope-min',
          valueTestId: 'slope-min-value',
        },
        {
          label: t('measurements.polyline.max'),
          value: data.maxSlope,
          testId: 'slope-max',
          valueTestId: 'slope-max-value',
        },
        {
          label: t('measurements.polyline.average'),
          value: data.averageSlope,
          testId: 'slope-average',
          valueTestId: 'slope-average-value',
        },
      ],
      testId: 'slope-values',
    },
  ];

  return (
    <Show when={summaryData()}>
      {(data) => (
        <div
          class="flex flex-col gap-3 p-3"
          data-testid="polyline-measurement-details"
        >
          <MeasurementProperty
            icon="tc-icon-measure"
            title={t('measurements.distance')}
            resultRows={distanceData(data())}
            testId="distance-section"
            infoButtonConfig={props.infoButtonConfig}
          />
          <MeasurementProperty
            icon="svg-vertical"
            title={t('measurements.polyline.vertical')}
            resultRows={verticalData(data())}
            testId="vertical-section"
            infoButtonConfig={props.infoButtonConfig}
          />
          <MeasurementProperty
            icon="tc-icon-measure-slope"
            title={t('measurements.polyline.slopeAndGrade')}
            resultRows={slopeAndGradeData(data())}
            testId="slope-section"
            dropdownOptions={slopeDropdownOptions}
            selectedDropdownValue={selectedSlopeFormat()}
            onDropdownChange={(value) => {
              handleSlopeFormatChange(value);
            }}
            infoButtonConfig={props.infoButtonConfig}
            disableDropdown={props.disableEdit}
            disabledReason={props.disabledReason}
          />
        </div>
      )}
    </Show>
  );
};
